import { timers } from "jquery";
import * as _ from "lodash";
import * as log from "loglevel";

import { action, autorun, computed, observable, runInAction } from "mobx";
import { config } from "../config";
import { Agent } from "../services/agent";
import { QueryString } from "../services/query";
import { App } from "./models/app";
import { EnumData } from "./models/enum-data";
import { Project } from "./models/project";
import { User } from "./models/user";
import { RootStore } from "./root-store";
import { Sort, UIStore } from "./ui-store";

export class ProjectStore {
	@observable projects: Project[] = [];
	@observable pinnedProjects: Project[] = [];
	@observable loading = false;
	@observable errorMessage: string = null;
	@observable accessTypes: EnumData[] = [];
	@observable syncNonSurveyLoading = false;

	@observable pageNumber: number = 0;
	@observable pageCount: number = 0;
	@observable pageSize: number = 18;
	@observable total: number = 0;
	@observable pages = new Map();

	@observable order: Sort = Sort.desc;
	@observable searchTerm: string = null;

	@observable pmList: Array<{name: string, emailAdress: string, fullName: string}> = [];

	@observable currentSearch: Date;

	@computed get currentPage(): Project[]{
		let page = this.pages.get(this.pageNumber) ? this.pages.get(this.pageNumber).slice() : [];
		return _.filter(this.projects.slice(), (p) => {
			return _.findIndex(page, (pg) => pg == p.id) > -1;
		});
	}

	@action async loadAccessTypes(){
		let results = await Agent.Projects.getAccessTypes();
		runInAction(() => {
			this.accessTypes = results.map(res => { return new EnumData(res); });
		});
	}

	@action async loadPinnedProjects(ids: string[]){
		try {
			let projectData: Array<any> = await Promise.all([...ids.map(async id => {
				try{
					return await Agent.Projects.get(id);
				}
				catch(err){
					log.error(err, "removing project from pins");
					await RootStore.uiStore.unpinProject(id);
					return null;
				}
			}), ...[Agent.User.getUserApps(ids)]]);

			let appData: IProjectAppsResponse =  projectData.pop();

			projectData = projectData.filter(p => p !== null);
			runInAction(() => {
				this.pinnedProjects = projectData.map(projectData => {
					let foundPermData = [];
					if(appData.projects && appData.projects[projectData.id]){
						let projectPermissions = appData.projects[projectData.id];
						projectPermissions.apps.forEach(appId => {
							let foundApp = appData.apps[appId];
							foundPermData.push({
								"id": appId,
								"token": projectPermissions.token,
								"disabled": false,
								"name": foundApp.name
							});
						})
						projectPermissions.disabledApps.forEach(appId => {
							let foundApp = appData.apps[appId];
							foundPermData.push({
								"id": appId,
								"token": projectPermissions.token,
								"disabled": true,
								"name": foundApp.name
							});
						})
					}
					return new Project(projectData, foundPermData);
				});
			});
		} catch (err) {
			runInAction(() => {
				this.errorMessage = err.message;
			});
		}
		finally{
			runInAction(() => {
			});
		}
	}

	findById(id: string): Project{
		return _.find(this.projects.slice(), {"id": id});
	}

	@action setLoading(state: boolean){
		this.loading = state;
	}

	@action async loadPmList() {
		let result : Array<any> = await Agent.Projects.getManager();
		runInAction(() => {
			this.pmList = result;
		});
	}

	@action async loadMemberProjects(pageNumber: number = 1, forceReload = false, statusFilter = [], pmFilter= []){
		try{
			this.loading = true;
			let results = await Agent.Projects.search(this.generateQuery(pageNumber), this.searchTerm, JSON.stringify(statusFilter), JSON.stringify(pmFilter));
			let permissions: IProjectAppsResponse = null;
			if(results && results.data && results.total > 0 && results.pageNumber && results.pageSize && results.pageCount){
				permissions = await Agent.User.getUserApps(results.data.map(p => p.id));
			}

			if(forceReload){
				this.pages.clear();
			}
			runInAction(() => {
				this.updateProjects(results, permissions);
			});
		}
		catch(err){
			runInAction(() => {
				this.errorMessage = err.message;
			});
		}
		finally{
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	@action updateProjects(pageData, permissionData: IProjectAppsResponse | null = null){
		if(pageData && pageData.data && pageData.total > 0 && pageData.pageNumber && pageData.pageSize && pageData.pageCount){
			let projects = pageData.data.map((res) => {
				let foundPermData = [];
				if(permissionData && permissionData.projects && permissionData.projects[res.id]){
					let proj = permissionData.projects[res.id];
					proj.apps.forEach(appId => {
						let foundApp = permissionData.apps[appId];
						foundPermData.push({
							"id": appId,
							"token": proj.token,
							"disabled": false,
							"name": foundApp.name
						});
					})
					proj.disabledApps.forEach(appId => {
						let foundApp = permissionData.apps[appId];
						foundPermData.push({
							"id": appId,
							"token": proj.token,
							"disabled": true,
							"name": foundApp.name
						});
					})
				}
				
				return new Project(res, foundPermData)
			});
			projects.forEach(project => {
				let index = _.findIndex(this.projects, {"id": project.id});
				// remove the old one if exist
				if(index > -1) {
					this.projects.splice(index, 1);
				}
				this.projects.push(project);
			});
			this.pages.set(pageData.pageNumber, projects.map(p => p.id));
			this.total = pageData.total;
			this.pageNumber = pageData.pageNumber;
			this.pageSize = pageData.pageSize;
			this.pageCount = pageData.pageCount;
		}
		else{
			this.pages.clear();
			this.total = pageData.total;
			this.pageNumber = pageData.pageNumber;
			this.pageSize = pageData.pageSize;
			this.pageCount = pageData.pageCount;
		}

	}

	@action changePage(page: number | string, statusFilters, pmFilters){
		let pageNumber = _.isNumber(page) ? page : parseInt(page);
		this.loadMemberProjects(pageNumber, true, statusFilters, pmFilters);
	}

	@action async syncProjects(){
		this.loading = true;
		await Agent.Projects.sync();
		await this.loadMemberProjects(1, true);
		runInAction(() => {
			this.loading = false;
		});
	}

	@action async syncNonSurveyData() {
		this.syncNonSurveyLoading = true;
		await Agent.Projects.syncNonSurveyData();

		runInAction(() => {
			this.syncNonSurveyLoading = false;
		});
	}

	generateQuery(pageNumber: number = 1){
		return new QueryString({
			"take": this.pageSize,
			"skip": this.pageSize * (pageNumber - 1),
			"order": { "field": "surveyId", "direction": this.order == Sort.desc ? "DESC" : "ASC"}
		});
	}

	@action async setOrder(order: Sort, reload = false){
		this.order = order;
		if(reload){
			await this.loadMemberProjects(1, true);
		}
	}

	@action clearSearch(){
		this.searchTerm = null;
	}

	@action setSearch(term: string){
		this.searchTerm = term;
	}

	@action async runSearch(){
		let cs = new Date();
		this.currentSearch = cs;

		let results = await Agent.Projects.search(this.generateQuery(), this.searchTerm);

		if(this.currentSearch !== cs){
			return;
		}

		let permissions: IProjectAppsResponse = null;
		if(results && results.data && results.total > 0 && results.pageNumber && results.pageSize && results.pageCount){
			permissions = await Agent.User.getUserApps(results.data.map(p => p.id));
		}

		if(this.currentSearch !== cs){
			return;
		}

		runInAction(() => {
			this.updateProjects(results, permissions);
		});
	}

	@action async create(body: {projectName: string, projectNumber: string, companyName: string}){
		this.loading = true;
		await Agent.Projects.create(body);
		await this.loadMemberProjects(1, true);
		runInAction(() => {
			this.loading = false;
		});
	}
}

export interface IProjectAppsResponse{
	apps: {
		[appId: string]: {
			url: string;
			settingsUrl?: string;
			name: string;
		}
	},
	projects: {
		[projectId: string]: {
			token: string;
			apps: Array<string>;
			disabledApps: Array<string>
		}
	}
}


// WEBPACK FOOTER //
// ./src/stores/project-store.ts